import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { SystemPlan } from '@cfa/pathway-fe-shared/xp';
import { Divider, Heading, SearchInput, ToggleButton } from '@cfa/react-core';

import { useAuth } from '@/hooks/useAuth';
import { type AddSortFields, useSortOptions } from '@/hooks/useSortOptions';

import { arrayToCommaString } from '@/utils/arrayToCommaString';
import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';

import DropdownWithTitle from '@/components/DropdownWithTitle';
import PathwayModal from '@/components/popups/PathwayModal';

import Constants from '@/constants';

import { TemplateCard } from '../components/TemplateCard';

import {
  DataViewAtomFamilyKey,
  sourceDataFamily,
  useDataView,
} from '@/atoms/data-view';
import { TranslationKey } from '@/i18n/i18n';

const DATA_VIEW_KEY = DataViewAtomFamilyKey.CHOOSE_TEMPLATE;

const CATEGORY_ORDER = [
  'compliance',
  'back_of_house',
  'front_of_house',
  'general',
  'hospitality',
  'leadership',
  'onboarding',
];

// https://cfacorp.atlassian.net/browse/PATHWAY-3661 remove when the BE filters out food safety plans for CA and PR users
const FOOD_SAFETY_IDS = new Set([
  'crn:pathway:plan:01KH20ZHAPPPX6S35X0TGCYP4X',
  'crn:pathway:plan:01KTKRSVWVDG3YSV9Q7G0G93WC',
  'crn:pathway:plan:01KHP5W6MA9FHW9G6G8ECHQH0K',
  'crn:pathway:plan:01KKCF2QJB025CY9W93QS5NXMF',
]);

type TemplateRow = AddSortFields<SystemPlan>;

interface ChooseTemplateModalStepProps {
  onCopy: (id: string) => void;
  onPreviewTemplateClick: (template: SystemPlan) => void;
  onAdd: (template: SystemPlan) => void;
  subscribingTemplateId?: string;
  isPending?: boolean;
  isLoadingTemplates?: boolean;
}

const ChooseTemplateModalStep: React.FC<ChooseTemplateModalStepProps> = ({
  onCopy,
  onPreviewTemplateClick,
  onAdd,
  subscribingTemplateId,
  isPending = false,
  isLoadingTemplates = false,
}) => {
  const { t, i18n } = useTranslation();
  const { data } = useAuth();
  const sortOptions = useSortOptions({ chooseTemplateModal: true });
  const sourceTemplates = useAtomValue(
    sourceDataFamily<TemplateRow>(DATA_VIEW_KEY),
  );

  const [
    {
      display: { items: visibleTemplates },
      filters,
      sort,
    },
    { setSearch, resetSearch, setSort, toggleCheckboxFilter },
  ] = useDataView<TemplateRow>(DATA_VIEW_KEY);

  const selectedSort = sort ?? sortOptions[0];
  const selectedCategories = filters.in.category ?? [];
  const formattedSelectedCategories = arrayToCommaString(
    CATEGORY_ORDER.filter(category =>
      selectedCategories.includes(category),
    ).map(category => t(mapCategoryToAliasTranslation(category))),
    t('Generic.and'),
    i18n.language.startsWith(Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE),
  );

  // Chip presence should ignore search/category filters, but still hide plans
  // that auth/display filtering would remove for this user.
  const presentCategories = CATEGORY_ORDER.filter(category =>
    sourceTemplates.some(plan => {
      if (plan.category !== category) return false;
      // https://cfacorp.atlassian.net/browse/PATHWAY-3622
      if (data?.isLicensee && plan.readonly) return false;
      if (
        (data?.country.id === 'CA' || data?.country.id === 'PR') &&
        FOOD_SAFETY_IDS.has(plan.id)
      ) {
        return false;
      }
      return true;
    }),
  );

  const featuredTemplates = visibleTemplates.filter(plan => plan.featured);
  const allTemplates = visibleTemplates.filter(plan => !plan.featured);
  const showSectionHeadings =
    featuredTemplates.length > 0 && allTemplates.length > 0;

  const renderSortDropdown = () => (
    <DropdownWithTitle
      label={t('TrainingPlans.filtering.sortBy')}
      options={sortOptions}
      value={sortOptions.find(
        option =>
          option.key === selectedSort?.key &&
          option.direction === selectedSort?.direction,
      )}
      displayKey="translationString"
      onSelectionChange={setSort}
      className="h-10 min-h-10! w-36 sm:h-auto sm:min-h-12! sm:w-48"
      containerClassName="gap-0"
      labelClassName="hidden whitespace-nowrap text-center sm:block"
    />
  );

  const renderTemplateSection = (
    titleKey: TranslationKey,
    templates: TemplateRow[],
    testId: string,
  ) => {
    if (templates.length === 0) return null;

    return (
      <div className="flex flex-col gap-4" data-testid={testId}>
        {showSectionHeadings && (
          <Heading level={3} variant="overline1">
            {t(titleKey)}
          </Heading>
        )}
        {templates.map(template => (
          <TemplateCard
            key={template.id}
            onCopy={() => {
              if (template.id) {
                onCopy(template.id);
              }
            }}
            onPreview={() => {
              onPreviewTemplateClick(template);
            }}
            onAdd={() => onAdd(template)}
            plan={template}
            isLoading={isPending && subscribingTemplateId === template.id}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <PathwayModal.Header noPadding>
        <PathwayModal.Title>{t('Button.choosePlan')}</PathwayModal.Title>
        <div className="flex shrink-0 flex-col gap-2">
          <div className="flex w-full items-end gap-7 px-4 sm:px-8">
            <SearchInput
              className="w-full"
              data-testid="template-search-input"
              value={filters.search}
              onChange={value =>
                setSearch(value, ['nameLocalLang', 'descriptionSearch'])
              }
              onClear={() => resetSearch()}
              placeholder={t('TrainingPlans.templates.searchTemplates')}
            />
            <div className="hidden sm:block">{renderSortDropdown()}</div>
          </div>
          {presentCategories.length > 0 && (
            <div
              className="flex min-h-10 w-full items-end pl-4 sm:items-center sm:px-0 sm:pt-4"
              data-testid="template-category-filters"
            >
              <div className="flex shrink-0 items-center sm:hidden">
                {renderSortDropdown()}
                <Divider
                  orientation="vertical"
                  className="border-grayscale-gray2! mx-3! h-6! self-center!"
                />
              </div>
              <div
                className="scrollbar-hide flex min-w-0 flex-1 gap-3 overflow-x-auto pr-4 sm:px-8"
                role="group"
                aria-label={t('TrainingPlans.templates.categoryFilters')}
              >
                {presentCategories.map(category => (
                  <ToggleButton
                    key={category}
                    size="sm"
                    variant="text"
                    isSelected={selectedCategories.includes(category)}
                    onChange={() => toggleCheckboxFilter('category', category)}
                    data-testid={`template-category-${category}`}
                    className="min-h-10 px-6! py-2! text-nowrap"
                  >
                    {t(mapCategoryToAliasTranslation(category))}
                  </ToggleButton>
                ))}
              </div>
            </div>
          )}
          <div className="px-4! sm:px-8!">
            <Divider className="border-black!" />
          </div>
          {selectedCategories.length > 0 && (
            <div className="px-4 sm:px-8">
              {t('TrainingPlans.templates.categoryFilterResults', {
                count: visibleTemplates.length,
              })}{' '}
              <b>{formattedSelectedCategories}</b>
            </div>
          )}
        </div>
      </PathwayModal.Header>
      <PathwayModal.Body noMinHeight>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-8 text-left">
          {visibleTemplates.length === 0 && !isLoadingTemplates ? (
            <p data-testid="template-search-no-results">
              {t('Search.noResults')}
            </p>
          ) : (
            <>
              {renderTemplateSection(
                'TrainingPlans.templates.featuredPlans',
                featuredTemplates,
                'template-featured-plans',
              )}
              {renderTemplateSection(
                'TrainingPlans.templates.allPlans',
                allTemplates,
                'template-all-plans',
              )}
            </>
          )}
        </div>
      </PathwayModal.Body>
    </>
  );
};

export default ChooseTemplateModalStep;
